import type { AbilityDef } from "../sim";
import type { Route } from "./routes";

/**
 * Вес пути бота по положению (консультант 05.11.2026, по слову Джона «в этом же и смысл смотреть на игру ботов что бы
 * увидеть максимум возможностей»): издалека — выстрел и рывок, вплотную — захват и локоть, после блока — серия, против
 * приседа — низкие, в углу — уход, издёвка, когда соперник далеко или сбит. Всё из данных приёма, не по списку имён.
 * Новизна: чем реже приём был в этом бою, тем охотнее бот его берёт — за матч видно всё.
 */
export interface Situation {
  /** Между бойцами, пиксели. */
  readonly distance: number;
  /** Соперник присел (низкие удары проходят под его блоком). */
  readonly crouching: boolean;
  /** Бот только что отбил удар в блоке — время ответить серией. */
  readonly afterBlock: boolean;
  /** Бот зажат у стены, соперник рядом. */
  readonly cornered: boolean;
  readonly meter: number;
  /** Бот сбит и лежит. */
  readonly lying: boolean;
  /** Соперник сбит или брошен — время для издёвки. */
  readonly opponentDown: boolean;
}

/** Издалека — дальше стольких пикселей; рядом — ближе MID. */
export const FAR = 260;
export const MID = 150;
/** Новизна: не взятый ни разу в бою приём весит в 1 + NOVELTY раз больше, взятый n раз — в 1 + NOVELTY / (1 + n)². */
const NOVELTY = 20;
/** Ближний приём (захват, локоть) бот берёт и чуть дальше: сначала подойдёт (bot/plan.ts). */
export const APPROACH = 80;

/** Докуда достаёт удар от стоп бойца, пиксели: зона удара плюс подача и ход клипа до активных кадров. */
export function reachOf(ability: AbilityDef): number {
  const box = ability.hitbox ? ability.hitbox.x + ability.hitbox.w : 0;
  // подача работает, только если у приёма нет хода корня (sim/fighter.ts steer)
  const advance = ability.motion ? 0 : (ability.advance ?? 0) * (ability.startup + ability.active);
  // ход корня клипа — прирост за кадр (sim/fighter.ts): путь до первого активного кадра — сумма
  const motion = (ability.motion ?? []).slice(0, ability.startup).reduce((sum, step) => sum + step, 0);
  return box + advance + motion;
}

/** Вес пути: 0 — сейчас невозможен; uses — сколько раз бот брал этот приём в бою. */
export function weightOf(route: Route, ability: AbilityDef, situation: Situation, uses: number): number {
  const { needs } = route;
  const { distance } = situation;
  if (needs.lying !== situation.lying) return 0;
  if (needs.meter > situation.meter) return 0;
  if (needs.near !== undefined && distance > needs.near + APPROACH) return 0;
  if (needs.far !== undefined && distance < needs.far) return 0;
  const input = ability.input;
  let weight = 1;
  if (input.taunt) weight = situation.opponentDown ? 1.5 : distance > FAR ? 1 : 0;
  else if (input.grab) weight = 5;
  else if (input.grabWhiff) weight = 0.2;
  else if (ability.projectile) weight = distance > FAR ? 6 : distance > MID ? 1.5 : 0.3;
  else if (input.run) weight = distance > FAR ? 4 : distance > MID ? 1 : 0;
  else if (ability.hit) weight = strikeWeight(ability, distance);
  else if (input.gesture)
    weight = (ability.cost ?? 0) > 0 ? (situation.cornered ? 6 : 0.4) : situation.cornered ? 0 : 0.6;
  if (input.near !== undefined) weight *= 4;
  if (ability.hit?.height === "low" && situation.crouching) weight *= 5;
  if (route.chain.length > 0) weight *= situation.afterBlock ? 5 : 0.8;
  if (input.charge !== undefined) weight *= distance > MID ? 1.2 : 0.4;
  return weight * (1 + NOVELTY / (1 + uses) ** 2);
}

/** Удар: достаёт — как есть, чуть дальше — реже (подойдёт в ударе), далеко — почти никогда; с подачей — рывок издалека. */
function strikeWeight(ability: AbilityDef, distance: number): number {
  const reach = reachOf(ability);
  const lunge = (ability.advance ?? 0) > 0 && distance > MID ? 3 : 1;
  if (distance <= reach + 20) return lunge;
  if (distance <= reach + 120) return 0.4 * lunge;
  return 0.05 * lunge;
}
